import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, App as AntApp, message, Spin } from 'antd';
import './App.css';
import MainLayout from './components/layout/MainLayout';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ROLES, hasRole, hasAnyRole } from './constants/roles';
import ProtectedRoute from './components/auth/ProtectedRoute';
import useAuthStore from './store/authStore';
import { useAuthInit } from './hooks/useAuthInit';
import { theme } from './theme';

// Lazy load components
const SubmitManuscript = lazy(() => import('./components/manuscript/SubmitManuscript'));
const BrowsePublications = lazy(() => import('./components/manuscript/BrowsePublications'));
const AuthenticatedBrowse = lazy(() => import('./components/manuscript/AuthenticatedBrowse'));
const ManuscriptView = lazy(() => import('./components/manuscript/ManuscriptView'));
const Home = lazy(() => import('./components/Home'));
const PublicHome = lazy(() => import('./components/public/PublicHome'));
const Configuration = lazy(() => import('./components/admin/Configuration'));
const UserManagement = lazy(() => import('./components/admin/UserManagement'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const DOIConfiguration = lazy(() => import('./components/admin/DOIConfiguration'));
const EmailTemplates = lazy(() => import('./components/admin/EmailTemplates'));
const EmailTemplateEditor = lazy(() => import('./components/admin/EmailTemplateEditor'));
const DOIManagementPage = lazy(() => import('./components/doi/DOIManagementPage'));
const Search = lazy(() => import('./components/Search'));
const MyReviews = lazy(() => import('./components/review/MyReviews'));
const MySubmissions = lazy(() => import('./components/manuscript/MySubmissions'));
const ChiefEditorManuscripts = lazy(() => import('./components/manuscript/ChiefEditorManuscripts'));
const Profile = lazy(() => import('./components/profile/Profile'));
const ReviewInvitation = lazy(() => import('./components/review/ReviewInvitation'));
const BrowseJournals = lazy(() => import('./components/journal/BrowseJournals'));
const PublicBrowseJournals = lazy(() => import('./components/journal/PublicBrowseJournals'));
const PublicJournalDetails = lazy(() => import('./components/journal/PublicJournalDetails'));
const ManageJournals = lazy(() => import('./components/journal/ManageJournals'));
const JournalApprovals = lazy(() => import('./components/journal/JournalApprovals'));
const JournalDetails = lazy(() => import('./components/journal/JournalDetails'));
const ManageVolumeIssues = lazy(() => import('./components/journal/ManageVolumeIssues'));
const SubmitPublication = lazy(() => import('./components/publication/SubmitPublication'));
const ChiefEditorPublications = lazy(() => import('./components/publication/ChiefEditorPublications'));
const PublicPublications = lazy(() => import('./components/publication/PublicPublications'));
const PublicationDetailView = lazy(() => import('./components/publication/PublicationDetailView'));
const AuthenticatedPublicationView = lazy(() => import('./components/publication/AuthenticatedPublicationView'));
const CommitteeList = lazy(() => import('./components/committee/CommitteeList'));
const CommitteeManagement = lazy(() => import('./components/committee/CommitteeManagement'));
const FAQ = lazy(() => import('./components/faq/FAQ'));
const AnnouncementManagement = lazy(() => import('./components/admin/AnnouncementManagement'));
const AnnouncementDetailPage = lazy(() => import('./components/announcement/AnnouncementDetailPage'));
const Notifications = lazy(() => import('./components/notification/Notifications'));

const PrivateRoute = ({ element: Element, requiredRoles }) => {
  return (
    <ProtectedRoute requiredRoles={requiredRoles}>
      <MainLayout>
        <Suspense fallback={<Spin size="large" tip="Loading..." />}>
          <Element />
        </Suspense>
      </MainLayout>
    </ProtectedRoute>
  );
};

const PublicRoute = ({ element: Element }) => {
  return (
    <Suspense fallback={<Spin size="large" tip="Loading..." />}>
      <Element />
    </Suspense>
  );
};

function App() {
  const { initialized } = useAuthInit();

  if (!initialized) {
    return (
      <ConfigProvider theme={theme}>
        <AntApp>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw'
          }}>
            <Spin size="large" tip="Initializing application..." />
          </div>
        </AntApp>
      </ConfigProvider>
    );
  }

  return (
    <ConfigProvider theme={theme}>
      <AntApp>
        <AuthProvider>
          <Router>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<PublicRoute element={PublicHome} />} />
            <Route path="/login" element={<PublicRoute element={PublicHome} />} />
            <Route path="/register" element={<PublicRoute element={PublicHome} />} />
            {/* Public browse */}
            <Route path="/browse" element={
              <Suspense fallback={<Spin size="large" tip="Loading..." />}>
                <BrowsePublications />
              </Suspense>
            } />
            <Route path="/publications-public" element={
              <Suspense fallback={<Spin size="large" tip="Loading..." />}>
                <PublicPublications />
              </Suspense>
            } />
            <Route path="/publications-public/:id" element={
              <Suspense fallback={<Spin size="large" tip="Loading..." />}>
                <PublicationDetailView />
              </Suspense>
            } />
            <Route path="/journals-public" element={
              <Suspense fallback={<Spin size="large" tip="Loading..." />}>
                <PublicBrowseJournals />
              </Suspense>
            } />
            <Route path="/journals-public/:id" element={
              <Suspense fallback={<Spin size="large" tip="Loading..." />}>
                <PublicJournalDetails />
              </Suspense>
            } />
            <Route path="/reviewer/accept-invitation" element={
              <Suspense fallback={<Spin size="large" tip="Loading..." />}>
                <ReviewInvitation />
              </Suspense>
            } />
            <Route path="/reviewer/decline-invitation" element={
              <Suspense fallback={<Spin size="large" tip="Loading..." />}>
                <ReviewInvitation />
              </Suspense>
            } />
            <Route path="/committee" element={
              <Suspense fallback={<Spin size="large" tip="Loading..." />}>
                <CommitteeList />
              </Suspense>
            } />
            <Route path="/faq" element={<PublicRoute element={FAQ} />} />
            <Route path="/documentation" element={<Navigate to="/faq" replace />} />
            <Route path="/announcements/:id" element={<PublicRoute element={AnnouncementDetailPage} />} />

            {/* Protected routes - accessible to all authenticated users */}
            <Route path="/dashboard" element={<PrivateRoute element={Home} />} />
            <Route path="/manuscripts" element={<PrivateRoute element={AuthenticatedBrowse} />} />
            <Route path="/submit" element={<PrivateRoute element={SubmitManuscript} />} />
            <Route path="/submit-manuscript" element={<PrivateRoute element={SubmitManuscript} />} />
            <Route path="/submit-manuscript/:id" element={<PrivateRoute element={SubmitManuscript} />} />
            <Route path="/my-submissions" element={<PrivateRoute element={MySubmissions} />} />
            <Route path="/manuscripts/:id" element={<PrivateRoute element={ManuscriptView} />} />
            <Route path="/publications/submit" element={<PrivateRoute element={SubmitPublication} requiredRoles={[ROLES.CHIEF_EDITOR, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/publications/submit/:id" element={<PrivateRoute element={SubmitPublication} requiredRoles={[ROLES.CHIEF_EDITOR, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/profile" element={<PrivateRoute element={Profile} />} />
            <Route path="/notifications" element={<PrivateRoute element={Notifications} />} />
            {/*<Route path="/search" element={<PrivateRoute element={Search} />} />*/}

            {/* Role-specific routes */}
            <Route path="/reviews" element={<PrivateRoute element={MyReviews} requiredRoles={[ROLES.REVIEWER]} />} />
            <Route path="/journal-manuscripts" element={<PrivateRoute element={ChiefEditorManuscripts} requiredRoles={[ROLES.CHIEF_EDITOR, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/publications" element={<PrivateRoute element={ChiefEditorPublications} requiredRoles={[ROLES.CHIEF_EDITOR, ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/publications/:id" element={<PrivateRoute element={AuthenticatedPublicationView} />} />

            {/* Journal routes */}
            <Route path="/journals" element={<PrivateRoute element={BrowseJournals} />} />
            <Route path="/journals/manage" element={<PrivateRoute element={ManageJournals} requiredRoles={[ROLES.EDITOR, ROLES.CHIEF_EDITOR, ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/journals/volumes" element={<PrivateRoute element={ManageVolumeIssues} requiredRoles={[ROLES.EDITOR, ROLES.CHIEF_EDITOR, ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/journals/volumes/:journalId" element={<PrivateRoute element={ManageVolumeIssues} requiredRoles={[ROLES.EDITOR, ROLES.CHIEF_EDITOR, ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/journals/issues" element={<Navigate to="/journals/volumes" replace />} />
            <Route path="/journals/issues/:journalId" element={<Navigate to="/journals/volumes" replace />} />
            <Route path="/journals/approvals" element={<PrivateRoute element={JournalApprovals} requiredRoles={[ROLES.CHIEF_EDITOR, ROLES.MANAGING_CHIEF_EDITOR, ROLES.ADMIN]} />} />
            <Route path="/journals/:id" element={<PrivateRoute element={JournalDetails} />} />

            {/* Admin routes */}
            <Route path="/admin" element={<PrivateRoute element={AdminDashboard} requiredRoles={[ROLES.ADMIN]} />} />
            <Route path="/admin/configuration" element={<PrivateRoute element={Configuration} requiredRoles={[ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/admin/users" element={<PrivateRoute element={UserManagement} requiredRoles={[ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/admin/doi" element={<PrivateRoute element={DOIConfiguration} requiredRoles={[ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/admin/committee" element={<PrivateRoute element={CommitteeManagement} requiredRoles={[ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/admin/email-templates" element={<PrivateRoute element={EmailTemplates} requiredRoles={[ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/admin/email-templates/:id" element={<PrivateRoute element={EmailTemplateEditor} requiredRoles={[ROLES.MANAGING_CHIEF_EDITOR]} />} />
            <Route path="/admin/announcements" element={<PrivateRoute element={AnnouncementManagement} requiredRoles={[ROLES.ADMIN, ROLES.MANAGING_CHIEF_EDITOR]} />} />

            {/* DOI Management routes */}
            <Route path="/doi/management" element={<PrivateRoute element={DOIManagementPage} requiredRoles={[ROLES.CHIEF_EDITOR, ROLES.MANAGING_CHIEF_EDITOR, ROLES.ADMIN]} />} />
            </Routes>
          </Router>
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
